import { useId } from 'react';
import { useNotifications } from '@/application/usecases/useNotifications';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { EyeOffIcon, SettingsIcon } from '@/ui/icons';
import { WidgetSection } from '@/features/dashboard/WidgetSection';
import { NotificationList } from './NotificationList';
import { useNotificationCenter } from './notificationCenterStore';
import { useOpenNotification } from './useOpenNotification';
import styles from './HomeNotifications.module.css';

const text = strings.notifications.home;

/** How many the home screen shows. The centre has the rest. */
export const HOME_NOTIFICATIONS = 5;

export interface HomeNotificationsProps {
  /**
   * Takes the section off the screen it is on. With it the section carries a
   * control for that beside its settings; without it, it stays where it is.
   */
  onHide?: () => void;
  /**
   * Drawn under a widget's heading, level with the widgets beside it, where it
   * stands on a Dashboard. Otherwise the small heading it has always had.
   */
  asWidget?: boolean;
}

/**
 * The Notifications section of a person's home screen. The request of
 * 7 October 2026, section 2.
 *
 * Their latest five, of the types they chose to see here: everything they
 * receive until they choose. View all opens the centre, and the control beside
 * it opens their settings, where what shows here is chosen and kept on their
 * account. A row opens its case in place, as it does in the centre.
 *
 * A widget of the Dashboard and of the Overview, and drawn by the shell over
 * the home screen of somebody whose home is neither, a technician at their
 * queue among them. Wherever it stands it can be taken off, by the control in
 * its heading, and put back: the lab's answer of 12 October 2026, "make the
 * notification on the home screen stay, but also removed. it is actually good
 * UX if they want it". Off the screen, a person's notifications are where they
 * always were, under the bell.
 */
export function HomeNotifications({ onHide, asWidget = false }: HomeNotificationsProps = {}) {
  const headingId = useId();
  const { data, isPending, isError, error } = useNotifications({ home: true, pageSize: HOME_NOTIFICATIONS });
  const show = useNotificationCenter((state) => state.show);
  const showSettings = useNotificationCenter((state) => state.showSettings);
  const open = useOpenNotification();

  /*
   * Over a screen, nothing to say while loading or after a failure: the screen
   * under it is what the person came for. Beside other widgets it keeps its
   * place whatever happens, since an empty place there is a hole in the row.
   */
  if (!asWidget && (isPending || isError)) return null;
  const rows = (data ?? []).slice(0, HOME_NOTIFICATIONS);

  const controls = (
    <>
      <button
        type="button"
        className={styles.icon}
        aria-label={text.choose}
        title={text.choose}
        onClick={showSettings}
      >
        <SettingsIcon />
      </button>
      {onHide !== undefined && (
        <button type="button" className={styles.icon} aria-label={text.hide} title={text.hide} onClick={onHide}>
          <EyeOffIcon />
        </button>
      )}
      <button type="button" className={styles.all} onClick={show}>
        {text.viewAll}
      </button>
    </>
  );

  const list = isPending ? (
    <SkeletonRows rows={2} label={strings.common.loading} />
  ) : isError ? (
    <p className={styles.empty}>{failureWords(error, strings.errors.server)}</p>
  ) : rows.length === 0 ? (
    <p className={styles.empty}>{text.empty}</p>
  ) : (
    <NotificationList rows={rows} onOpen={open} label={text.listLabel} />
  );

  // A region named by its heading either way, so it is found by name wherever it stands.
  if (asWidget) {
    return (
      <section aria-labelledby={headingId}>
        <WidgetSection
          label={strings.dashboard.kicker.you}
          title={text.title}
          titleId={headingId}
          action={controls}
        >
          {list}
        </WidgetSection>
      </section>
    );
  }

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <div className={styles.head}>
        <h3 id={headingId}>{text.title}</h3>
        <span className={styles.spacer} />
        {controls}
      </div>
      {list}
    </section>
  );
}
